/**
 * This module implements additional security protections for the auth service
 */
// Access environment variables in a way that works in the shared library context
const getEnvironment = () => {
  // Default values for development
  return {
    production: false,
    qa: false
  };
};

/**
 * Custom encoding function to protect sensitive strings in compiled code
 * This makes the code more difficult to reverse engineer
 */
export class SecurityEncoder {
  // Simple XOR encryption with a random key for each string
  static encode(input: string): string {
    // This will be replaced with the actual key during build time
    const key = this.generateKey(input.length);
    let result = '';
    
    // Add extra obfuscation by XORing each character
    for (let i = 0; i < input.length; i++) {
      const charCode = input.charCodeAt(i) ^ key[i % key.length].charCodeAt(0);
      result += String.fromCharCode(charCode);
    }
    
    // Convert to a less readable format
    return btoa(result).split('').reverse().join('');
  }
  
  static decode(encoded: string): string {
    // Reverse the encoding process
    const reversed = encoded.split('').reverse().join('');
    const xorEncoded = atob(reversed);
    
    // This will be replaced with the actual key during build time
    const key = this.generateKey(xorEncoded.length);
    let result = '';
    
    // Decode each character
    for (let i = 0; i < xorEncoded.length; i++) {
      const charCode = xorEncoded.charCodeAt(i) ^ key[i % key.length].charCodeAt(0);
      result += String.fromCharCode(charCode);
    }
    
    return result;
  }
  
  private static generateKey(length: number): string {
    // Generate a deterministic but hard-to-predict key
    // In production, this will use a proper key derivation
    const env = getEnvironment();
    // Ensure we respect the requested length but cap to reasonable bounds
    const size = Math.max(1, Math.min(length || 10, 32));
    const ua = (typeof navigator !== 'undefined' && navigator?.userAgent) ? navigator.userAgent : 'fallback';
    return env.production ?
      'PRD_' + btoa(ua).substring(0, size) :
      'DEV_' + 'testkey123456'.substring(0, size);
  }
}

/**
 * Anti-tampering protection for auth service
 * Detects if the code has been modified by checking integrity
 */
export class CodeIntegrityVerifier {
  static verifyIntegrity(): boolean {
    const env = getEnvironment();
    if (!env.production && !env.qa) {
      return true; // Skip in development
    }
    
    try {
      // This check uses multiple techniques to detect browser dev tools and debugging
      const devToolsOpen = this.detectDevTools();
      if (devToolsOpen) {
        console.warn('Security warning: Unexpected environment detected');
        // In production this would take additional actions
        return false;
      }
      
      // Additional integrity checks would go here in a real implementation
      return true;
    } catch (e) {
      // If there's an error, assume tampering
      console.error('Security error: Code integrity check failed');
      return false;
    }
  }
  
  private static detectDevTools(): boolean {
    // Collection of heuristics to detect developer tools
    const debuggerEnabled = function() {
      // Avoid using the `debugger` statement to comply with lint rules
      return false;
    };
    
    // Check for console overrides (common in tampering)
    const consoleTest = function() {
      const originalConsole = { 
        log: console.log, 
        warn: console.warn, 
        error: console.error 
      };
      
      let modified = false;
      for (const key in originalConsole) {
        if (console[key].toString() !== originalConsole[key].toString()) {
          modified = true;
          break;
        }
      }
      
      return modified;
    };
    
    return debuggerEnabled() || consoleTest();
  }
}
